/**
 * Photo storage backed by IndexedDB.
 *
 * Inspection photos are too big for localStorage, so we keep them in a
 * dedicated IndexedDB database. The page reads/writes via this module's
 * promise-based API.
 *
 * Extracted from /app/frontend/src/pages/CleaningInspection.js (Feb 2026)
 * as part of the giant-component refactor. Behavior is byte-for-byte
 * equivalent.
 */

// IndexedDB Configuration for extended photo storage
const DB_NAME = 'MaidlyInspectionPhotos';
const DB_VERSION = 1;
const PHOTO_STORE = 'photos';
export const STORAGE_LIMIT = 100 * 1024 * 1024; // 100MB limit

const initDB = () =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(PHOTO_STORE)) {
        const store = db.createObjectStore(PHOTO_STORE, { keyPath: 'id' });
        store.createIndex('inspectionId', 'inspectionId', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });

export const photoStorage = {
  async save(photoData) {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([PHOTO_STORE], 'readwrite');
      const store = transaction.objectStore(PHOTO_STORE);
      const request = store.put(photoData);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  },

  async get(photoId) {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([PHOTO_STORE], 'readonly');
      const store = transaction.objectStore(PHOTO_STORE);
      const request = store.get(photoId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  },

  async getByInspection(inspectionId) {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([PHOTO_STORE], 'readonly');
      const store = transaction.objectStore(PHOTO_STORE);
      const index = store.index('inspectionId');
      const request = index.getAll(inspectionId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  },

  async delete(photoId) {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([PHOTO_STORE], 'readwrite');
      const store = transaction.objectStore(PHOTO_STORE);
      const request = store.delete(photoId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  },

  async getStorageUsed() {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([PHOTO_STORE], 'readonly');
      const store = transaction.objectStore(PHOTO_STORE);
      const request = store.getAll();
      request.onsuccess = () => {
        const photos = request.result;
        const totalSize = photos.reduce(
          (acc, photo) => acc + (photo.data?.length || 0),
          0
        );
        resolve({ size: totalSize, count: photos.length });
      };
      request.onerror = () => reject(request.error);
    });
  },

  async clearOldPhotos(daysOld = 30) {
    const db = await initDB();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([PHOTO_STORE], 'readwrite');
      const store = transaction.objectStore(PHOTO_STORE);
      const index = store.index('timestamp');
      const range = IDBKeyRange.upperBound(cutoffDate.toISOString());
      const request = index.openCursor(range);
      let deletedCount = 0;
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          deletedCount++;
          cursor.continue();
        } else {
          resolve(deletedCount);
        }
      };
      request.onerror = () => reject(request.error);
    });
  },
};
